import React from 'react';

import { Stack, Stepper } from '@mantine/core';

import WizardFooter from './WizardFooter';
import WizardStep, { WizardStepProps } from './WizardStep';
import { useCancelModal } from '../../CancelModalContext';

interface WizardComposition {
  Step: typeof WizardStep;
}

interface WizardProps {
  children: React.ReactNode;
}

const Wizard: React.FC<WizardProps> & WizardComposition = ({ children }) => {
  const { currentStep, visibleSteps } = useCancelModal();

  // Discover step children + filter out skipped ones. The provider's visibleSteps is the
  // source of truth for which steps actually render — we use it to look up the active node.
  const stepNodes = React.Children.toArray(children).filter(
    (node): node is React.ReactElement<WizardStepProps> =>
      React.isValidElement(node) && node.type === WizardStep,
  );

  const activeNode = stepNodes.find(node => node.props.name === currentStep && !node.props.skip);

  const activeIndex = Math.max(0, visibleSteps.indexOf(currentStep));

  return (
    <Stack spacing='md'>
      <Stepper active={activeIndex} size='sm' breakpoint='sm' allowNextStepsSelect={false}>
        {visibleSteps.map(step => (
          <Stepper.Step key={step} label={step} />
        ))}
      </Stepper>
      {activeNode}
      <WizardFooter />
    </Stack>
  );
};

Wizard.Step = WizardStep;

export default Wizard;
